// Park Loop — global theme JS. Ported from the Astro inline scripts
// (Header, ProductCard, ProductGallery, ProductDetails, SizeGuide, StickyAtc).
// Vanilla, no framework. Each block guards on element presence.
(() => {
  // ---- Header: mobile menu + scroll state ----
  const header = document.querySelector("[data-site-header]");
  const toggle = document.querySelector("[data-menu-toggle]");
  const panel = document.querySelector("[data-menu-panel]");
  if (toggle && panel) {
    const setOpen = (open) => {
      toggle.setAttribute("aria-expanded", String(open));
      toggle.classList.toggle("is-open", open);
      panel.classList.toggle("is-open", open);
      document.body.style.overflow = open ? "hidden" : "";
    };
    toggle.addEventListener("click", () =>
      setOpen(toggle.getAttribute("aria-expanded") !== "true"),
    );
    panel.querySelectorAll("a").forEach((a) =>
      a.addEventListener("click", () => setOpen(false)),
    );
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") setOpen(false);
    });
  }
  if (header) {
    const onScroll = () =>
      header.classList.toggle("is-scrolled", window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  // ---- Product card colourway swatches ----
  document.querySelectorAll("[data-swatches]").forEach((group) => {
    const img = group.parentElement?.querySelector("[data-card-img]");
    if (!img) return;
    const swatches = group.querySelectorAll(".swatch");
    swatches.forEach((sw) => {
      const swap = () => { if (sw.dataset.swatchSrc) img.src = sw.dataset.swatchSrc; };
      sw.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        swap();
        swatches.forEach((s) => {
          const active = s === sw;
          s.classList.toggle("active", active);
          s.setAttribute("aria-pressed", active ? "true" : "false");
        });
      });
      sw.addEventListener("mouseenter", swap);
    });
  });

  // ---- Product gallery (thumbs + arrows + colour filter) ----
  const gallery = document.querySelector("[data-gallery]");
  if (gallery) {
    const mainImg = gallery.querySelector("[data-main-img]");
    const allThumbs = [...gallery.querySelectorAll("[data-thumb-idx]")];
    if (mainImg && allThumbs.length >= 2) {
      let current = 0;
      const show = (idx) => {
        current = ((idx % allThumbs.length) + allThumbs.length) % allThumbs.length;
        const thumb = allThumbs[current];
        mainImg.src = thumb.dataset.thumbSrc;
        mainImg.alt = thumb.dataset.thumbAlt;
        allThumbs.forEach((t) => t.classList.toggle("active", t === thumb));
      };
      allThumbs.forEach((t) =>
        t.addEventListener("click", () => show(allThumbs.indexOf(t))),
      );
      gallery.querySelector("[data-prev]")?.addEventListener("click", () => show(current - 1));
      gallery.querySelector("[data-next]")?.addEventListener("click", () => show(current + 1));
      // Colour change jumps the main image to that colour's shot; thumbs stay visible.
      window.addEventListener("pl:color-change", (e) => {
        const color = e.detail?.color?.toLowerCase();
        if (!color) return;
        const idx = allThumbs.findIndex(
          (t) => t.dataset.thumbColor?.toLowerCase() === color,
        );
        if (idx >= 0) show(idx);
      });
    }
  }

  // ---- Product variant picker + real add-to-cart wiring ----
  const form = document.querySelector("#pdp-form");
  const variantsEl = document.querySelector("[data-pdp-variants]");
  if (form && variantsEl) {
    let variants = [];
    try { variants = JSON.parse(variantsEl.textContent); } catch (_) {}
    // Translated UI strings, injected by main-product.liquid (fall back to English).
    const S = window.plStrings || {};
    const unitsLeft = (n) => (S.unitsLeft || "__n__ left").replace("__n__", n);
    const hiddenId = form.querySelector("[data-variant-id]");
    const priceEl = document.querySelector("[data-price]");
    const skuEl = document.querySelector("[data-sku]");
    const atc = form.querySelector("[data-pdp-atc]");
    const atcLabel = form.querySelector("[data-atc-label]");
    const hint = form.querySelector("[data-stock-hint]");
    const money = (cents) =>
      new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "CAD",
        maximumFractionDigits: 0,
      }).format(cents / 100);

    const selectedOptions = () => {
      const groups = [...form.querySelectorAll("[data-option-group]")];
      const opts = [];
      groups.forEach((g) => {
        const pos = parseInt(g.dataset.optionPosition, 10);
        const checked = g.querySelector("input:checked");
        opts[pos - 1] = checked ? checked.value : null;
      });
      return opts;
    };
    const findVariant = (opts) =>
      variants.find((v) =>
        opts.every((o, i) => o == null || v.options[i] === o),
      );
    const colorGroup = form.querySelector('[data-option-role="color"]');
    const currentColor = () =>
      colorGroup?.querySelector("input:checked")?.value ?? null;

    const updateSizeAvailability = () => {
      const color = currentColor();
      form.querySelectorAll("[data-size-chip]").forEach((chip) => {
        const size = chip.dataset.sizeValue;
        const match = variants.find(
          (v) => v.options.includes(size) && (color == null || v.options.includes(color)),
        );
        const soldOut = !match || !match.available;
        chip.classList.toggle("sold-out", soldOut);
        chip.classList.toggle("is-backorder", Boolean(match && match.backorder));
        const input = chip.querySelector("input");
        if (input) {
          input.disabled = soldOut;
          if (soldOut && input.checked) input.checked = false;
        }
      });
    };

    const isBackorder = (v) => Boolean(v && v.backorder);
    const update = () => {
      const variant = findVariant(selectedOptions());
      if (priceEl && variant) priceEl.textContent = money(variant.price);
      if (skuEl && variant) skuEl.textContent = variant.sku || "";
      if (hiddenId && variant) hiddenId.value = variant.id;
      const buyable = variant && variant.available;
      const back = isBackorder(variant);
      if (atc) { atc.disabled = !buyable; atc.classList.toggle("is-preorder", back); }
      if (atcLabel)
        atcLabel.textContent = !variant
          ? (S.selectOptions || "Select options")
          : back
            ? (S.preorder || "Pre-order") + " — " + money(variant.price)
            : buyable
              ? (S.addToCart || "Add to cart") + " — " + money(variant.price)
              : (S.soldOut || "Sold out");
      if (hint) {
        hint.classList.toggle("is-preorder", back);
        if (!variant) hint.textContent = "";
        else if (!variant.available) hint.textContent = S.soldOut || "Sold out";
        else if (back) hint.textContent = S.preorderNote || S.preorder || "";
        else if (typeof variant.inventory_quantity === "number" && variant.inventory_quantity > 0)
          hint.textContent = unitsLeft(variant.inventory_quantity);
        else hint.textContent = S.inStock || "In stock";
      }
    };

    form.querySelectorAll("[data-option-group] input").forEach((r) =>
      r.addEventListener("change", (e) => {
        if (e.target.name === "option" + (colorGroup?.dataset.optionPosition)) {
          window.dispatchEvent(
            new CustomEvent("pl:color-change", { detail: { color: e.target.value } }),
          );
        }
        updateSizeAvailability();
        update();
      }),
    );
    updateSizeAvailability();
    update();
    // Sync the gallery to the colour pre-selected via ?variant= on load.
    if (colorGroup) {
      const c = currentColor();
      if (c) window.dispatchEvent(new CustomEvent("pl:color-change", { detail: { color: c } }));
    }
  }

  // ---- Size guide drawer ----
  const sgTrigger = document.querySelector("[data-size-guide-open]");
  const sgDialog = document.querySelector("[data-size-guide-dialog]");
  if (sgTrigger && sgDialog) {
    sgTrigger.addEventListener("click", () => sgDialog.showModal());
    sgDialog.addEventListener("click", (e) => {
      if (!e.target.closest(".drawer-inner")) sgDialog.close();
    });
  }

  // ---- Sticky mobile ATC ----
  const sticky = document.querySelector("[data-sticky-atc]");
  const anchor = document.querySelector("[data-pdp-atc]");
  if (sticky && anchor && form) {
    const label = sticky.querySelector("[data-sticky-atc-label]");
    const btn = sticky.querySelector("[data-sticky-atc-btn]");
    const strings = window.plStrings || {};
    const sizeFieldset = form.querySelector("fieldset:has(input[name^='option'])");
    const syncLabel = () => {
      const size = form.querySelector('[data-option-role="size"] input:checked');
      if (label) label.textContent = size ? (strings.addToCart || "Add to cart") : (strings.selectSize || "Select size");
      sticky.classList.toggle("is-ready", Boolean(size) && !anchor.disabled);
    };
    const io = new IntersectionObserver(
      ([entry]) => { sticky.hidden = entry.isIntersecting; },
      { rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(anchor);
    btn?.addEventListener("click", () => {
      const size = form.querySelector('[data-option-role="size"] input:checked');
      if (!size && sizeFieldset) {
        sizeFieldset.scrollIntoView({ behavior: "smooth", block: "center" });
        sizeFieldset.querySelector("input:not([disabled])")?.focus();
        return;
      }
      anchor.click();
    });
    form.querySelectorAll('[data-option-role="size"] input').forEach((r) =>
      r.addEventListener("change", syncLabel),
    );
    syncLabel();
  }
})();

// ---- Collection filters: auto-submit on change (noscript falls back to Apply) ----
(() => {
  const form = document.querySelector("[data-filters]");
  if (!form) return;
  form.addEventListener("change", () => form.submit());
})();
